<template>
  <DModal
    :show-close="false"
    :isBgOverlay="true"
    :appendToBody="true"
    :placeInTop="true"
    :isCloseOnEsc="!isSubjectModal"
    :isCloseOnBgClick="!isSubjectModal"
    @close="$emit('close')"
  >
    <div class="sdl text-dark" slot="insertComponent">
      <div class="sdl__header">
        <h2>Brikker på {{ dayTitle }}</h2>
        <div class="sdl__header__actions">
          <button ref="createButton" class="do-bttn" @click="$emit('create')">Opret flere brikker</button>
          <div class="font-large icon__bg-circle sdl__header__close" @click="$emit('close')">
            <span class="icon-cross2"></span>
          </div>
        </div>
      </div>

      <div class="sdl__content">
        <p v-if="subjects.length === 0" class="sdl__empty">Der er ikke oprettet nogen brikker for denne dag endnu.</p>

        <table v-else class="do-table do-table--striped sdl__table">
          <thead>
            <tr>
              <th class="sdl__col-name">Brik</th>
              <th class="sdl__col-classes">Klasser</th>
              <th class="sdl__col-teachers">Undervisere</th>
              <th class="sdl__col-hours">Timer</th>
              <th class="sdl__col-actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="subject in subjects" :key="subject.id">
              <td class="sdl__col-name">
                <span class="sdl__pill" :style="{backgroundColor: subject.color}">{{ subject.name }}</span>
              </td>
              <td class="sdl__col-classes">{{ classNames(subject) || '–' }}</td>
              <td class="sdl__col-teachers">{{ teacherNames(subject) || '–' }}</td>
              <td class="sdl__col-hours">{{ formatHours(subject.hours) }}</td>
              <td class="sdl__col-actions">
                <template v-if="confirmingDeleteId === subject.id">
                  <span class="sdl__confirm-text">Slet brik?</span>
                  <button class="do-bttn do-bttn--small do-bttn--danger"
                          :class="{'do-bttn--loading': isDeleting}"
                          :disabled="isDeleting"
                          @click="runDelete(subject)">Slet</button>
                  <button class="do-bttn do-bttn--small" :disabled="isDeleting" @click="confirmingDeleteId = null">Fortryd</button>
                </template>
                <template v-else>
                  <button class="do-bttn do-bttn--small" aria-label="Rediger brik"
                          @click="startEdit(subject)"
                          v-tippy="{arrow: true, placement: 'bottom'}" :content="'Rediger brik'">
                    <span class="icon-pencil"></span>
                  </button>
                  <button v-if="canDeleteSubject(subject)"
                          class="do-bttn do-bttn--small do-bttn--danger" aria-label="Slet brik"
                          @click="confirmingDeleteId = subject.id"
                          v-tippy="{arrow: true, placement: 'bottom'}" :content="'Slet brik'">
                    <span class="icon-bin"></span>
                  </button>
                  <button v-else
                          class="do-bttn do-bttn--small do-bttn--disabled" aria-label="Kan ikke slettes"
                          disabled
                          v-tippy="{arrow: true, placement: 'bottom'}" :content="'Kan ikke slettes, da brikken er skemalagt'">
                    <span class="icon-bin"></span>
                  </button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="sdl__buttons">
        <button class="do-bttn do-bttn--submit" :disabled="subjects.length === 0" @click="openSchedule">Skemalæg brikker</button>
        <button class="do-bttn" @click="$emit('close')">Luk</button>
      </div>

      <SpecialDayScheduleModal
        v-if="isScheduleModal"
        :schoolId="schoolId"
        :specialDay="specialDay"
        :subjects="subjects"
        :klassCalendars="klassCalendars"
        @scheduled="onScheduled"
        @close="isScheduleModal = false">
      </SpecialDayScheduleModal>

      <SubjectForm
        v-if="isSubjectModal"
        :showModal="isSubjectModal"
        :editSubject="editSubject"
        :propIsLoading="isSubjectLoading"
        :allowDanishPreparationFactorUi="true"
        @deleteSubject="handleDeleteSubject"
        @close="closeSubjectModal"
        @updateSubject="updateSubject"
        @saveAndShowTab="saveAndShowTab">
      </SubjectForm>
    </div>
  </DModal>
</template>

<script>
import eventbus from '../../eventbus'
import docendo from '../../docendo'
import DModal from '../common/DModal'
import SubjectForm from '../Subjects/SubjectForm'
import SpecialDayScheduleModal from './SpecialDayScheduleModal'

export default {
  name: 'special-day-subjects-list-modal',
  components: {DModal, SubjectForm, SpecialDayScheduleModal},
  props: {
    schoolId: {
      default: null
    },
    schoolYearId: {
      default: null
    },
    specialDay: {
      type: Object,
      default: () => ({})
    },
    klassCalendars: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      editSubject: null,
      isSubjectModal: false,
      isSubjectLoading: false,
      isScheduleModal: false,
      confirmingDeleteId: null,
      isDeleting: false
    }
  },
  computed: {
    subjectsInHash () {
      return this.$store.getters.subjectsInHash
    },
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },
    klassHash () {
      return this.klassCalendars.reduce((obj, klass) => {
        obj[klass.id] = klass
        return obj
      }, {})
    },
    dayTitle () {
      return this.specialDay && this.specialDay.title ? this.specialDay.title : 'omlagt dag'
    },
    subjects () {
      const ids = (this.specialDay && this.specialDay.subject_ids) || []
      return ids
        .map(id => this.subjectsInHash[id])
        .filter(subject => !!subject)
    }
  },
  mounted () {
    document.body.classList.add('no-scroll')
  },
  beforeDestroy () {
    document.body.classList.remove('no-scroll')
  },
  methods: {
    canDeleteSubject (subject) {
      return subject.can_delete !== false
    },
    formatHours (hours) {
      const num = parseFloat(hours)
      return isNaN(num) ? '0' : String(num)
    },
    classNames (subject) {
      const ids = []
      if (subject.calendar_id) {
        ids.push(subject.calendar_id)
      }
      if (Array.isArray(subject.calendar_ids)) {
        subject.calendar_ids.forEach(id => ids.push(id))
      }
      const seen = {}
      const names = []
      ids.forEach(id => {
        if (seen[id] || !this.klassHash[id]) {
          return
        }
        seen[id] = true
        names.push(this.klassHash[id].name)
      })
      return names.join(', ')
    },
    teacherNames (subject) {
      const ids = Array.isArray(subject.calendar_ids) ? subject.calendar_ids : []
      const seen = {}
      const names = []
      ids.forEach(id => {
        const cal = this.allCalendarsInHash[id]
        if (seen[id] || !cal || cal.type !== 'user' || !cal.name) {
          return
        }
        seen[id] = true
        names.push(cal.name)
      })
      return names.join(', ')
    },
    openSchedule () {
      if (this.subjects.length === 0) {
        return
      }
      this.isScheduleModal = true
    },
    onScheduled () {
      this.isScheduleModal = false
      this.$emit('changed')
    },
    startEdit (subject) {
      this.openSubjectForm(subject.id)
    },
    openSubjectForm (subjectId, tab) {
      this.confirmingDeleteId = null
      this.isSubjectLoading = true
      this.isSubjectModal = true
      docendo.showSubject(subjectId, success => {
        this.editSubject = tab ? Object.assign({}, success, {_openTab: tab}) : Object.assign({}, success)
        this.isSubjectLoading = false
      }, () => {
        this.isSubjectLoading = false
        this.isSubjectModal = false
        this.editSubject = null
        eventbus.toast = {text: 'Kunne ikke hente brikken', success: false}
      })
    },
    closeSubjectModal () {
      this.isSubjectModal = false
      this.editSubject = null
    },
    updateSubject (updatedSubject) {
      if (!updatedSubject.schoolId) {
        updatedSubject.schoolId = this.schoolId
      }
      if (!updatedSubject.schoolYearId) {
        updatedSubject.schoolYearId = this.schoolYearId
      }
      this.$store.dispatch('updateSubject', updatedSubject)
      this.closeSubjectModal()
      this.$emit('changed')
    },
    saveAndShowTab (updatedSubject, tab) {
      const subjectId = updatedSubject.id
      this.updateSubject(updatedSubject)
      setTimeout(() => {
        this.openSubjectForm(subjectId, tab)
      }, 500)
    },
    handleDeleteSubject (subject) {
      const deletedSubject = {
        id: subject.id,
        school_id: subject.school_id || this.schoolId,
        school_year_id: this.schoolYearId,
        schoolYearId: this.schoolYearId
      }
      this.$store.dispatch('deleteSubject', deletedSubject)
      this.closeSubjectModal()
      this.$emit('changed')
    },
    runDelete (subject) {
      if (this.isDeleting) {
        return
      }
      if (!this.canDeleteSubject(subject)) {
        this.confirmingDeleteId = null
        eventbus.toast = {text: 'Brikken kan ikke slettes, da den er skemalagt', success: false}
        return
      }
      this.isDeleting = true
      docendo.deleteSubject(subject.id, this.schoolId, this.schoolYearId, data => {
        const deleted = typeof data === 'string' ? JSON.parse(data) : data
        this.isDeleting = false
        this.confirmingDeleteId = null
        if (!deleted || !deleted.id) {
          eventbus.toast = {text: 'Brikken kan ikke slettes, da den er skemalagt', success: false}
          this.$emit('changed')
          return
        }
        this.$store.commit('deleteSubject', deleted)
        eventbus.toast = {text: 'Brikken blev slettet', success: true}
        this.$emit('changed')
      }, () => {
        this.isDeleting = false
        eventbus.toast = {text: 'Brikken kunne ikke slettes', success: false}
      })
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.sdl {
  width: calc(100vw - 4rem);
  max-width: 880px;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.sdl__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #eee;
}

.sdl__header h2 {
  margin: 0;
}

.sdl__header__actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sdl__header__close {
  cursor: pointer;
}

.sdl__content {
  padding: 1rem 1.5rem;
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.sdl__empty {
  color: var(--grey);
  font-style: italic;
  margin: 0;
}

.sdl__table {
  width: 100%;
  table-layout: fixed;
}

.sdl__table th,
.sdl__table td {
  vertical-align: middle;
}

.sdl__col-name,
.sdl__col-classes,
.sdl__col-teachers,
.sdl__col-hours,
.sdl__col-actions {
  width: 20%;
}

.sdl__col-hours {
  text-align: right;
}

.sdl__col-actions {
  text-align: right;
  white-space: nowrap;
}

.sdl__col-actions .do-bttn {
  margin-left: 0.4rem;
}

.sdl__pill {
  display: inline-block;
  max-width: 100%;
  padding: 4px 8px;
  border-radius: 4px;
  color: #fff;
  font-weight: bold;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, .3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.sdl__confirm-text {
  font-size: 12px;
  color: var(--danger);
  margin-right: 0.25rem;
}

.sdl__buttons {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #eee;
}

.do-bttn.do-bttn--small {
  font-size: 11px;
  height: auto;
  padding: 5px 8px;
  display: inline-flex;
  align-items: center;
}

.do-bttn--danger {
  color: var(--danger);
}

.do-bttn.do-bttn--disabled {
  color: var(--grey);
  opacity: 0.5;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// SpecialDaySubjectsListModal.vue?17f65e44